import {makeAutoObservable} from 'mobx';
import {DisplayTypeEnum, RenderV2_DisplayItem, RenderV2Object} from './render-v2-d';
import {layoutDocHeightInches, layoutWidthInches, matOpeningInches, resolveDocumentSize} from './document-dimensions/document-dimensions';
import React from 'react';
import {gridStart, resolveMatrixLayout} from './matrix-layout/resolve-matrix-layout';

type SpiritSide = {
    top?: boolean;
    left?: boolean;
    bottom?: boolean;
    right?: boolean;
    vertical?: boolean;
};

export class FrameBuilderStore {
    public frame: RenderV2Object;
    public DPI = 0;

    // mat
    public matSize = 0.4;
    public halfMatSize = this.matSize / 2;
    public doubleMatSize = this.matSize * 2;

    // spirit
    public outerSpiritSize = 0.3;
    public innerSpiritSize = 0.12;
    public doubleInnerSpiritSize = this.innerSpiritSize * 2;

    constructor(frame: RenderV2Object) {
        this.frame = frame;
        makeAutoObservable(this);
    }

    get maxCols() {
        return Math.max(...this.frame.displayMatrix.map(r => r.length));
    }

    get colWidths() {
        return Array.from({length: this.maxCols}).map((_, colIdx) => Math.max(...this.frame.displayMatrix.map(row => (row[colIdx] ? layoutWidthInches(row[colIdx]) : 0))));
    }

    get rowHeights() {
        return this.frame.displayMatrix.map(row => Math.max(...row.map(item => layoutDocHeightInches(item))));
    }

    get matrixLayout() {
        return resolveMatrixLayout(this.frame.displayMatrix);
    }

    get contentWidth() {
        const columns = this.colWidths.reduce((sum, width) => sum + width, 0);
        const gaps = this.matrixLayout.columnGaps.reduce((sum, gap) => sum + gap, 0);

        return columns + this.frame.railLeft + this.frame.railRight + gaps;
    }

    get contentHeight() {
        const rows = this.rowHeights.reduce((sum, height) => sum + height, 0);
        const gaps = this.matrixLayout.rowGaps.reduce((sum, gap) => sum + gap, 0);

        return rows + this.frame.railTop + this.frame.railBottom + gaps;
    }

    get frameWidth() {
        return this.contentWidth + this.frame.moulding.mouldingThickness * 2;
    }

    get frameHeight() {
        return this.contentHeight + this.frame.moulding.mouldingThickness * 2;
    }

    get itemsByType(): Map<DisplayTypeEnum, RenderV2_DisplayItem[]> {
        const map = new Map<DisplayTypeEnum, RenderV2_DisplayItem[]>();

        this.frame.displayMatrix
            .flatMap(row => row)
            .forEach(item => {
                if (!item) return;

                const arr = map.get(item.displayType) || [];

                arr.push(item);
                map.set(item.displayType, arr);
            });

        return map;
    }

    get docRows() {
        return this.frame.displayMatrix.map(row => row.filter(item => item.displayType === DisplayTypeEnum.Doc)).filter(row => row.length > 0);
    }

    get isVertical() {
        return !this.docRows.some(row => row.length > 1);
    }

    get hasSpirit() {
        return !!this.frame.spiritMode;
    }

    get outerSides(): SpiritSide[] {
        return [
            {top: true, left: true},
            {top: true, right: true},
            {bottom: true, left: true},
            {bottom: true, right: true},
            {left: true, vertical: true},
            {right: true, vertical: true},
        ];
    }

    get spiritSides() {
        if (!this.hasSpirit) return [];

        return this.outerSides.map(side => ({
            outer: this.outerSideStyle(side),
            inner: this.innerSideStyle(side),
        }));
    }

    outerSideStyle = (side: SpiritSide): React.CSSProperties => {
        const {railLeft, railRight, railTop, railBottom, spiritMode} = this.frame;
        const os = this.outerSpiritSize;
        const is = this.innerSpiritSize;
        const dis = this.doubleInnerSpiritSize;
        const styles: React.CSSProperties = {background: spiritMode?.outerColor};

        if (side.vertical) {
            styles.top = this.inchToPx(railTop / 2 - dis);
            styles.left = side.left ? this.inchToPx(railLeft / 2 - dis) : 'auto';
            styles.right = side.right ? this.inchToPx(railRight / 2 - dis) : 'auto';
            styles.height = `calc(100% - ${this.inchToPx(railTop / 2 + railBottom / 2 - os - is)})`;
            styles.width = this.inchToPx(os);
        } else {
            const width = this.inchToPx(side.left ? railLeft / 2 : railRight / 2);
            const topCut = spiritMode?.topCutWidth ?? 0;
            const bottomCut = spiritMode?.bottomCutWidth ?? 0;
            const cut = side.top ? topCut / 2 : bottomCut / 2;

            styles.top = side.top ? this.inchToPx(railTop / 2 - dis) : 'auto';
            styles.bottom = side.bottom ? this.inchToPx(railBottom / 2 - dis) : 'auto';
            styles.height = this.inchToPx(os);
            styles.width = cut ? `calc(50% - ${width} - ${this.inchToPx(cut)})` : '50%';

            if (side.left) {
                styles.left = this.inchToPx(railLeft / 2 - dis);
            } else {
                styles.right = this.inchToPx(railRight / 2 - dis);
            }
        }

        return styles;
    };

    innerSideStyle = (side: SpiritSide): React.CSSProperties => {
        const {spiritMode} = this.frame;
        const is = this.innerSpiritSize;
        const dis = this.doubleInnerSpiritSize;
        const styles: React.CSSProperties = {background: spiritMode?.innerColor, zIndex: 1};

        if (side.vertical) {
            styles.height = `calc(100% - ${this.inchToPx(dis)})`;
            styles.width = this.inchToPx(is);
        } else {
            styles.height = this.inchToPx(is);
            styles.marginLeft = side.left ? this.inchToPx(is) : 'auto';
            styles.marginRight = side.right ? this.inchToPx(is) : 'auto';
            styles.width = `calc(100% - ${this.inchToPx(dis)})`;
        }

        return styles;
    };

    itemsCount = (type: DisplayTypeEnum) => {
        return this.itemsByType.get(type)?.length || 0;
    };

    /** Resolved space after this 0-based grid column. 0 on the last column and when the boundary declares no gap. */
    gapAfterColumn = (columnIndex: number) => this.matrixLayout.columnGaps[columnIndex] ?? 0;

    /** Resolved space after this 0-based grid row. 0 on the last row and when the boundary declares no gap. */
    gapAfterRow = (rowIndex: number) => this.matrixLayout.rowGaps[rowIndex] ?? 0;

    /** Trailing column gap for an item's CSS `columnStart`, not its displayMatrix array index. */
    gapAfterItemColumn = (item: RenderV2_DisplayItem) => this.gapAfterColumn(gridStart(item.columnStart) - 1);

    /** Trailing row gap for an item's CSS `rowStart`, not its displayMatrix array index. */
    gapAfterItemRow = (item: RenderV2_DisplayItem) => this.gapAfterRow(gridStart(item.rowStart) - 1);

    containerStyle = (item: RenderV2_DisplayItem) => {
        const {width, height} = resolveDocumentSize(item);

        return {
            width: this.inchToPx(width),
            height: this.inchToPx(height),
        };
    };

    matImageElements = (item: RenderV2_DisplayItem) => {
        if (!item.mat2ImageURL) return [];

        const {width, height} = resolveDocumentSize(item);
        // Rejected Docs (0×0) and undersized openings cannot host matSize rails: the
        // corners are still painted at matSize with overflow:visible and spill out.
        if (matOpeningInches(width, this.doubleMatSize) <= 0 || matOpeningInches(height, this.doubleMatSize) <= 0) {
            return [];
        }

        const {cornerTopLeftURL, cornerTopRightURL, cornerBottomRightURL, cornerBottomLeftURL, sideLeftURL, sideRightURL, sideTopURL, sideBottomURL} = item.mat2ImageURL;
        const offset = this.inchToPx(this.matSize);
        const innerSize = `calc(100% - ${this.inchToPx(this.matSize * 2)})`;

        const elements: {id: string; src: string; style: React.CSSProperties}[] = [
            {id: 'mat2_top_left', src: cornerTopLeftURL, style: {top: 0, left: 0, width: this.inchToPx(this.matSize)}},
            {id: 'mat2_top_right', src: cornerTopRightURL, style: {top: 0, right: 0, width: this.inchToPx(this.matSize)}},
            {id: 'mat2_bottom_right', src: cornerBottomRightURL, style: {bottom: 0, right: 0, width: this.inchToPx(this.matSize)}},
            {id: 'mat2_bottom_left', src: cornerBottomLeftURL, style: {bottom: 0, left: 0, width: this.inchToPx(this.matSize)}},
            {id: 'mat2_left', src: sideLeftURL, style: {top: offset, left: 0, height: innerSize, width: this.inchToPx(this.matSize)}},
            {id: 'mat2_right', src: sideRightURL, style: {top: offset, right: 0, height: innerSize, width: this.inchToPx(this.matSize)}},
            {id: 'mat2_top', src: sideTopURL, style: {top: 0, left: offset, width: innerSize, height: this.inchToPx(this.matSize)}},
            {id: 'mat2_bottom', src: sideBottomURL, style: {bottom: 0, left: offset, width: innerSize, height: this.inchToPx(this.matSize)}},
        ];

        return elements;
    };

    matBoxElements = (item: RenderV2_DisplayItem) => {
        const {width, height} = resolveDocumentSize(item);
        if (width <= 0 || height <= 0) return [];

        const {mat2Color} = item;

        return [
            {cls: 'mat2-outer', w: width, h: height},
            {cls: 'mat2-width absolute-center', w: matOpeningInches(width, this.halfMatSize), h: matOpeningInches(height, this.halfMatSize), extra: {background: mat2Color}},
            {cls: 'mat2-inner absolute-center', w: matOpeningInches(width, this.matSize + this.halfMatSize), h: matOpeningInches(height, this.matSize + this.halfMatSize)},
        ];
    };

    documentStyle = (item: RenderV2_DisplayItem) => {
        const {width, height} = resolveDocumentSize(item);
        const style: React.CSSProperties = {
            width: this.inchToPx(matOpeningInches(width, this.doubleMatSize)),
            height: this.inchToPx(matOpeningInches(height, this.doubleMatSize)),
        };

        if (item.mat2ImageURL) {
            style.top = this.inchToPx(this.matSize);
            style.left = this.inchToPx(this.matSize);
            style.position = 'absolute' as const;
        }

        return style;
    };

    inchToPx = (val: number) => {
        return `${val !== -1 ? val * this.DPI : 0}px`;
    };
}
